import { useState } from "react";
import { Wallet, ArrowRight, ArrowLeft, Check, Loader2, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useResgatePreview, useValidateResgate } from "@/hooks/useResgates";
import { toast } from "sonner";
import type { Resgate } from "@/types/api";

type Step = "input" | "preview" | "success";

interface PreviewState {
  influenciador_email: string;
  valor: string;
  tipo: string;
  code: string;
  product_name?: string | null;
  product_image_url?: string | null;
}

export default function SellerResgates() {
  const [step, setStep] = useState<Step>("input");
  const [code, setCode] = useState("");
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [result, setResult] = useState<Resgate | null>(null);
  const fetchPreview = useResgatePreview();
  const validate = useValidateResgate();

  function handlePreview() {
    const c = code.replace(/\s/g, "").trim();
    if (!c || !/^\d{6}$/.test(c)) {
      toast.error("Digite o código de 6 dígitos do indicador.");
      return;
    }
    fetchPreview.mutate(c, {
      onSuccess: (data) => {
        setPreview({ ...data, code: c });
        setStep("preview");
      },
      onError: (err: unknown) => {
        const msg =
          (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
          "Código inválido ou expirado.";
        toast.error(msg);
      },
    });
  }

  function handleConfirm() {
    if (!preview) return;
    validate.mutate(preview.code, {
      onSuccess: (data) => {
        setResult(data);
        setStep("success");
        toast.success("Resgate confirmado com sucesso!");
      },
      onError: (err: unknown) => {
        const msg =
          (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
          "Erro ao confirmar resgate.";
        toast.error(msg);
      },
    });
  }

  function handleReset() {
    setStep("input");
    setCode("");
    setPreview(null);
    setResult(null);
  }

  return (
    <div className="space-y-6 max-w-md mx-auto">
      <h1 className="text-2xl font-bold text-foreground">Registrar Resgate</h1>

      {step === "input" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Wallet className="h-5 w-5 text-primary" />
              Código de Resgate
            </CardTitle>
            <CardDescription>
              Solicite o código de 6 dígitos ao indicador (ex: 483 921).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="resgateCode">Código do resgate</Label>
              <Input
                id="resgateCode"
                placeholder="000 000"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="font-mono text-xl tracking-[0.3em] text-center"
                maxLength={7}
                inputMode="numeric"
                autoComplete="off"
                autoFocus
              />
            </div>
            <Button
              className="w-full gap-2"
              onClick={handlePreview}
              disabled={fetchPreview.isPending}
            >
              {fetchPreview.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>Verificar <ArrowRight className="h-4 w-4" /></>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {step === "preview" && preview && (
        <Card className="overflow-hidden">
          {preview.product_image_url && (
            <div className="w-full h-36 bg-muted">
              <img
                src={preview.product_image_url}
                alt={preview.product_name ?? "Produto"}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Wallet className="h-5 w-5 text-primary" />
              Confirmar Resgate
            </CardTitle>
            <CardDescription>Verifique os dados antes de confirmar.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg border bg-muted/30 p-4 space-y-3 text-sm">
              {preview.product_name && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Produto</span>
                  <span className="font-semibold">{preview.product_name}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground flex items-center gap-1">
                  <User className="h-3 w-3" />
                  Indicador
                </span>
                <span className="font-medium">{preview.influenciador_email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Valor</span>
                <span className="font-semibold">
                  {preview.tipo === "reais"
                    ? `R$ ${Number(preview.valor).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`
                    : `${Math.round(Number(preview.valor))} pts`}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Código</span>
                <span className="font-mono font-bold">
                  {preview.code.slice(0, 3)} {preview.code.slice(3)}
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1 gap-2"
                onClick={() => setStep("input")}
                disabled={validate.isPending}
              >
                <ArrowLeft className="h-4 w-4" />
                Voltar
              </Button>
              <Button
                className="flex-1 gap-2"
                onClick={handleConfirm}
                disabled={validate.isPending}
              >
                {validate.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                Confirmar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === "success" && result && (
        <Card className="border-green-500/30">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-green-600">
              <Check className="h-5 w-5" />
              Resgate Confirmado!
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg border bg-muted/30 p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Tipo</span>
                <span className="font-medium capitalize">{result.tipo}</span>
              </div>
              <div className="flex justify-between font-semibold">
                <span>Valor</span>
                <span>
                  {result.tipo === "reais"
                    ? `R$ ${Number(result.valor).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`
                    : `${Math.round(Number(result.valor))} pts`}
                </span>
              </div>
            </div>
            <Button className="w-full" onClick={handleReset}>
              Novo Resgate
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
